import { type User, getAuth, onAuthStateChanged } from "firebase/auth";
import { useEffect, useState } from "react";

/**
 * Hook to monitor Firebase authentication state changes
 *
 * This hook provides:
 * - The current Firebase user
 * - Loading state while checking authentication
 * - Error state if authentication check fails
 *
 * @returns Object containing the current Firebase user, loading state, and error
 */
export function useFirebaseAuthState() {
	const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);

	useEffect(() => {
		const auth = getAuth();

		// Set up the auth state change listener
		const unsubscribe = onAuthStateChanged(
			auth,
			(user) => {
				setFirebaseUser(user);
				setLoading(false);
			},
			(err) => {
				console.error("Firebase auth state change error", err);
				setError(err);
				setLoading(false);
			},
		);

		// Clean up the listener when the component unmounts
		return () => unsubscribe();
	}, []);

	return { firebaseUser, loading, error };
}
